import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../Component/Sidebar';
import StoreCreationModal from '../Component/StoreCreationModal';
import StoreNotesManager from '../Component/StoreNotesManager';
import { 
  fetchStoreOwnersThunk, 
  fetchStoreOwnerThunk,
  createStoreOwnerThunk, 
  updateStoreOwnerThunk 
} from '../features/storeOwners/storeOwnerThunks';
import { clearCurrentStoreOwner } from '../features/storeOwners/storeOwnerSlice';
import { 
  createStoreThunk, 
  updateStoreThunk,
  fetchStoreThunk
} from '../features/stores/storeThunks';
import { clearCurrentStore } from '../features/stores/storeSlice';
import '../CSS/StoreManagement.css';

// NEW: Incident types for incentive rates
const INCIDENT_TYPES = [
  { value: 'theftPrevented', label: 'Theft Prevented', icon: '🛡️' },
  { value: 'theftReported', label: 'Theft Reported', icon: '🚨' },
  { value: 'incidentReported', label: 'Incident Reported', icon: '📋' },
  { value: 'customerDenied', label: 'Customer Denied', icon: '🚫' },
  { value: 'cashierSuspicious', label: 'Cashier Suspicious', icon: '👁️' },
  { value: 'openingClosing', label: 'Opening/Closing', icon: '🏪' },
  { value: 'announcement', label: 'Announcement', icon: '📢' },
];

const StoreManagementForm = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  
  const ownerImageRef = useRef(null);
  const storeImageRef = useRef(null);
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [creationResponse, setCreationResponse] = useState(null);
  
  const [formData, setFormData] = useState({
    // User/owner fields
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    owner_image: null,
    owner_image_preview: null,
    
    // Store fields
    store_name: '',
    store_address: '',
    store_city: '',
    store_state: '',
    store_zipcode: '',
    store_phone_no: '',
    pos_name: '',
    pos_qty: '',
    store_category: '24x7',
    image: null,
    image_preview: null,
    nest_account: '',
    specialComments: '',
    incentive_amount: '', // DEPRECATED: Keep for backward compatibility
    
    // Email configuration fields
    approval_email_recipients: [],
    approval_email_cc: [],
    use_owner_email_fallback: true,
  });

  // NEW: Incentive rates state - separate amount for each incident type
  const [incentiveRates, setIncentiveRates] = useState({
    theftPrevented: '0.00',
    theftReported: '0.00',
    incidentReported: '0.00',
    customerDenied: '0.00',
    cashierSuspicious: '0.00',
    openingClosing: '0.00',
    announcement: '0.00',
  });

  const [emailInputs, setEmailInputs] = useState({
    newRecipient: '',
    newCC: ''
  });
  const [emailErrors, setEmailErrors] = useState({});

  const [loadExisting, setLoadExisting] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const dispatch = useDispatch();
  const { storeOwners, currentStoreOwner, loading: ownerLoading, error: ownerError } = useSelector(state => state.storeOwners);
  const { currentStore, loading: storeLoading, error: storeError } = useSelector(state => state.stores);
  
  const isLoading = ownerLoading || storeLoading;
  const isEditing = !!storeId;

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const addEmailRecipient = () => {
    const email = emailInputs.newRecipient.trim();
    if (!email) return;

    if (!validateEmail(email)) {
      setEmailErrors(prev => ({
        ...prev,
        newRecipient: 'Please enter a valid email address'
      }));
      return;
    }

    if (formData.approval_email_recipients.includes(email)) {
      setEmailErrors(prev => ({
        ...prev,
        newRecipient: 'This email is already in the recipients list'
      }));
      return;
    }

    if (formData.approval_email_cc.includes(email)) {
      setEmailErrors(prev => ({
        ...prev,
        newRecipient: 'This email is already in the CC list'
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      approval_email_recipients: [...prev.approval_email_recipients, email]
    }));

    setEmailInputs(prev => ({ ...prev, newRecipient: '' }));
    setEmailErrors(prev => ({ ...prev, newRecipient: '' }));
  };

  const removeEmailRecipient = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      approval_email_recipients: prev.approval_email_recipients.filter(email => email !== emailToRemove)
    }));
  };

  const addCCRecipient = () => {
    const email = emailInputs.newCC.trim();
    if (!email) return;

    if (!validateEmail(email)) {
      setEmailErrors(prev => ({
        ...prev,
        newCC: 'Please enter a valid email address'
      }));
      return;
    }

    if (formData.approval_email_cc.includes(email)) {
      setEmailErrors(prev => ({
        ...prev,
        newCC: 'This email is already in the CC list'
      }));
      return;
    }

    if (formData.approval_email_recipients.includes(email)) {
      setEmailErrors(prev => ({
        ...prev,
        newCC: 'This email is already in the recipients list'
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      approval_email_cc: [...prev.approval_email_cc, email]
    }));

    setEmailInputs(prev => ({ ...prev, newCC: '' }));
    setEmailErrors(prev => ({ ...prev, newCC: '' }));
  };

  const removeCCRecipient = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      approval_email_cc: prev.approval_email_cc.filter(email => email !== emailToRemove)
    }));
  };

  const handleEmailInputChange = (field, value) => {
    setEmailInputs(prev => ({ ...prev, [field]: value }));
    if (emailErrors[field]) {
      setEmailErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleEmailKeyPress = (e, type) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (type === 'recipient') {
        addEmailRecipient();
      } else if (type === 'cc') {
        addCCRecipient();
      }
    }
  };

  // NEW: Handle incentive rate changes
  const handleIncentiveRateChange = (incidentType, value) => {
    // Allow empty, numbers, and decimal points
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setIncentiveRates(prev => ({
        ...prev,
        [incidentType]: value
      }));
    }
  };

  // Calculate total potential earnings
  const calculateTotalIncentive = () => {
    return Object.values(incentiveRates).reduce((sum, val) => {
      const num = parseFloat(val) || 0;
      return sum + num;
    }, 0).toFixed(2);
  };
  
  useEffect(() => {
    if (storeId) {
      dispatch(fetchStoreThunk(storeId));
    } else {
      dispatch(clearCurrentStore());
      dispatch(clearCurrentStoreOwner());
    }
  }, [dispatch, storeId]);
  
  useEffect(() => {
    if (loadExisting) {
      dispatch(fetchStoreOwnersThunk());
    }
  }, [dispatch, loadExisting]);
  
  useEffect(() => {
    if (currentStore) {
      setFormData(prev => ({
        ...prev,
        store_name: currentStore.store_name || '',
        store_address: currentStore.store_address || '',
        store_city: currentStore.store_city || '',
        store_state: currentStore.store_state || '',
        store_zipcode: currentStore.store_zipcode || '',
        store_phone_no: currentStore.store_phone_no || '',
        pos_name: currentStore.pos_name || '',
        pos_qty: currentStore.pos_qty || '',
        store_category: currentStore.store_category || '24x7',
        nest_account: currentStore.nest_account || '',
        image_preview: currentStore.image || null,
        specialComments: currentStore.specialComments || '',
        incentive_amount: currentStore.incentive_amount || '',
        approval_email_recipients: currentStore.approval_email_recipients || [],
        approval_email_cc: currentStore.approval_email_cc || [],
        use_owner_email_fallback: currentStore.use_owner_email_fallback !== undefined ? currentStore.use_owner_email_fallback : true,
      }));

      // NEW: Load existing incentive rates
      if (currentStore.incentive_rates && Array.isArray(currentStore.incentive_rates)) {
        const rates = {};
        currentStore.incentive_rates.forEach(rate => {
          rates[rate.incident_type] = rate.amount;
        });
        setIncentiveRates(prevRates => ({
          ...prevRates,
          ...rates
        }));
      }
      
      if (currentStore.owner) {
        setSelectedOwnerId(typeof currentStore.owner === 'object' ? currentStore.owner.id : currentStore.owner);
      }
    }
  }, [currentStore]);
  
  useEffect(() => {
    if (selectedOwnerId) {
      dispatch(fetchStoreOwnerThunk(selectedOwnerId));
    }
  }, [dispatch, selectedOwnerId]);
  
  useEffect(() => {
    if (currentStoreOwner) {
      setFormData(prev => ({
        ...prev,
        first_name: currentStoreOwner.user?.first_name || '',
        last_name: currentStoreOwner.user?.last_name || '',
        email: currentStoreOwner.user?.email || '',
        phone_number: currentStoreOwner.phone_number || '',
        owner_image_preview: currentStoreOwner.image || null
      }));
    }
  }, [currentStoreOwner]);
  
  const handleModalClose = () => {
    setShowSuccessModal(false);
    setCreationResponse(null);
    navigate('/stores');
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleIncentiveChange = (e) => {
    const { value } = e.target;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFormData({
        ...formData,
        incentive_amount: value
      });
    }
  };
  
  const handleCategoryChange = (category) => {
    setFormData({
      ...formData,
      store_category: category
    });
  };
  
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      
      if (name === 'owner_image') {
        setFormData({
          ...formData,
          owner_image: file,
          owner_image_preview: previewUrl
        });
      } else if (name === 'image') {
        setFormData({
          ...formData,
          image: file,
          image_preview: previewUrl
        });
      }
    }
  };
  
  const handleToggleChange = () => {
    setLoadExisting(!loadExisting);
    if (!loadExisting) {
      setFormData(prev => ({
        ...prev,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        owner_image: null,
        owner_image_preview: null
      }));
    } else {
      setSelectedOwnerId(null);
    }
  };
  
  const handleOwnerSelect = (e) => {
    const ownerId = e.target.value;
    setSelectedOwnerId(ownerId === '' ? null : ownerId);
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.store_name) errors.store_name = "Store name is required";
    if (!formData.store_city) errors.store_city = "City is required";
    if (!formData.store_state) errors.store_state = "State is required";
    if (!formData.store_zipcode) errors.store_zipcode = "Zipcode is required";
    if (!formData.store_phone_no) errors.store_phone_no = "Phone number is required";
    
    if (isEditing) {
      if (loadExisting) {
        if (!selectedOwnerId) {
          errors.selectedOwnerId = "Please select a store owner";
        }
      } else {
        const hasOwnerInfo = formData.first_name || formData.last_name || formData.email;
        const hasCurrentOwner = currentStore?.owner;
        
        if (!hasOwnerInfo && !hasCurrentOwner) {
          errors.first_name = "First name is required";
          errors.last_name = "Last name is required";
          errors.email = "Email is required";
        } else if (hasOwnerInfo) {
          if (!formData.first_name) errors.first_name = "First name is required";
          if (!formData.last_name) errors.last_name = "Last name is required";
          if (!formData.email) errors.email = "Email is required";
        }
      }
    } else {
      if (loadExisting) {
        if (!selectedOwnerId) errors.selectedOwnerId = "Please select a store owner";
      } else {
        if (!formData.first_name) errors.first_name = "First name is required";
        if (!formData.last_name) errors.last_name = "Last name is required";
        if (!formData.email) errors.email = "Email is required";
      }
    }
    
    // NEW: Validate incentive rates
    Object.entries(incentiveRates).forEach(([type, amount]) => {
      const numAmount = parseFloat(amount);
      if (amount !== '' && (isNaN(numAmount) || numAmount < 0)) {
        errors[`incentive_${type}`] = "Must be a positive number";
      }
    });

    const hasRecipients = formData.approval_email_recipients.length > 0;
    const hasFallback = formData.use_owner_email_fallback;
    
    if (!hasRecipients && !hasFallback) {
      errors.email_config = "Either add email recipients or enable owner email fallback";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const createRequestData = (dataFields, includeFiles = true) => {
    const hasFiles = includeFiles && (formData.owner_image || formData.image);
    
    if (hasFiles) {
      const formDataObj = new FormData();
      
      Object.entries(dataFields).forEach(([key, value]) => {
        if (key === 'approval_email_recipients' || key === 'approval_email_cc') {
          formDataObj.append(key, JSON.stringify(value || []));
        } else if (key === 'set_incentive_rates') {
          // NEW: Add incentive rates as JSON
          formDataObj.append(key, JSON.stringify(value));
        } else if (value !== null && value !== undefined && value !== '') {
          formDataObj.append(key, value);
        }
      });
      
      if (includeFiles) {
        if (formData.owner_image instanceof File) {
          formDataObj.append('store_owner_image', formData.owner_image);
        }
        if (formData.image instanceof File) {
          formDataObj.append('image', formData.image);
        }
      }
      
      return formDataObj;
    } else {
      return dataFields;
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (isEditing) {
      const storeUpdates = {
        store_name: formData.store_name,
        store_address: formData.store_address,
        store_city: formData.store_city,
        store_state: formData.store_state,
        store_zipcode: formData.store_zipcode,
        store_phone_no: formData.store_phone_no,
        pos_name: formData.pos_name,
        pos_qty: formData.pos_qty,
        store_category: formData.store_category,
        nest_account: formData.nest_account,
        specialComments: formData.specialComments,
        incentive_amount: formData.incentive_amount ? parseFloat(formData.incentive_amount) : 0,
        approval_email_recipients: formData.approval_email_recipients,
        approval_email_cc: formData.approval_email_cc,
        use_owner_email_fallback: formData.use_owner_email_fallback,
        // NEW: Add incentive rates
        set_incentive_rates: incentiveRates,
      };

      if (loadExisting && selectedOwnerId) {
        storeUpdates.owner = selectedOwnerId;
      } else if (!loadExisting) {
        const hasOwnerInfo = formData.first_name || formData.last_name || formData.email;
        
        if (hasOwnerInfo) {
          storeUpdates.store_owner_email = formData.email;
          storeUpdates.store_owner_first_name = formData.first_name;
          storeUpdates.store_owner_last_name = formData.last_name;
          storeUpdates.store_owner_phone = formData.phone_number;
        } else if (currentStore?.owner) {
          storeUpdates.owner = typeof currentStore.owner === 'object' ? currentStore.owner.id : currentStore.owner;
        }
      } else if (currentStore?.owner) {
        storeUpdates.owner = typeof currentStore.owner === 'object' ? currentStore.owner.id : currentStore.owner;
      }
      
      const requestData = createRequestData(storeUpdates, true);
      
      const storeResponse = await dispatch(updateStoreThunk({
        storeId,
        updates: requestData
      }));
      
      if (updateStoreThunk.fulfilled.match(storeResponse)) {
        navigate('/stores');
      }
    } else {
      const storeData = {
        store_name: formData.store_name,
        store_address: formData.store_address,
        store_city: formData.store_city,
        store_state: formData.store_state,
        store_zipcode: formData.store_zipcode,
        store_phone_no: formData.store_phone_no,
        pos_name: formData.pos_name,
        pos_qty: formData.pos_qty,
        store_category: formData.store_category,
        nest_account: formData.nest_account,
        specialComments: formData.specialComments,
        incentive_amount: formData.incentive_amount ? parseFloat(formData.incentive_amount) : 0,
        approval_email_recipients: formData.approval_email_recipients,
        approval_email_cc: formData.approval_email_cc,
        use_owner_email_fallback: formData.use_owner_email_fallback,
        // NEW: Add incentive rates
        set_incentive_rates: incentiveRates,
      };

      if (loadExisting && selectedOwnerId) {
        storeData.owner = selectedOwnerId;
      } else {
        storeData.store_owner_email = formData.email;
        storeData.store_owner_first_name = formData.first_name;
        storeData.store_owner_last_name = formData.last_name;
        storeData.store_owner_phone = formData.phone_number;
      }
      
      const requestData = createRequestData(storeData, true);
      
      const storeResponse = await dispatch(createStoreThunk(requestData));
      
      if (createStoreThunk.fulfilled.match(storeResponse)) {
        const responseData = storeResponse.payload;
        setCreationResponse(responseData);
        setShowSuccessModal(true);
      }
    }
  };

  if (isLoading && isEditing && !currentStore) {
    return (
      <div className="incident-report-app">
        <div className="incident-report-content">
          <div className="incident-report-loading">
            <div className="incident-report-loader"></div>
            <p>Loading Store Data...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="store-creation-app incident-report-app">
      <div className="incident-report-content">
        <div className="incident-report-layout">
          <div className="incident-report-image-side">
            <div className="incident-report-image-content"></div>
            <button 
              type="button" 
              className="incident-report-back-btn"
              onClick={() => navigate('/stores')}
            >
              ← Back
            </button>
          </div>
          
          <div className="incident-report-form-side">
            <div className="incident-report-container">
              <div className="incident-report-header">
                <h1>{isEditing ? 'Update Store' : 'Create Your Store Account'}</h1>
              </div>

              {(ownerError || storeError) && (
                <div className="incident-report-section">
                  <div className="incident-report-error-message">
                    {ownerError || storeError}
                  </div>
                </div>
              )}

              {selectedOwnerId && currentStoreOwner && (
                <div className="incident-report-section">
                  <div className="incident-report-owner-preview">
                    <div className="incident-report-owner-image">
                      {currentStoreOwner.image ? (
                        <img src={currentStoreOwner.image} alt={`${currentStoreOwner.user?.first_name} ${currentStoreOwner.user?.last_name}`} />
                      ) : (
                        <div className="incident-report-owner-placeholder">
                          {currentStoreOwner.user?.first_name?.[0]}{currentStoreOwner.user?.last_name?.[0]}
                        </div>
                      )}
                    </div>
                    <div className="incident-report-owner-info">
                      <h3>{currentStoreOwner.user?.first_name} {currentStoreOwner.user?.last_name}</h3>
                      <p>{currentStoreOwner.user?.email}</p>
                      <p>{currentStoreOwner.phone_number}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="incident-report-section">
                  <h2>Store Owner Information</h2>
                  
                  <div className="incident-report-group">
                    <label>Add Existing Customer / Store</label>
                    <div className="incident-report-helper-text">Toggle to load list of existing customers.</div>
                    <div className="incident-report-radio-options">
                      <label className="incident-report-radio-option">
                        <input
                          type="checkbox"
                          checked={loadExisting}
                          onChange={handleToggleChange}
                        />
                        <div className="incident-report-radio-content">
                          <div className="incident-report-radio-check"></div>
                          <div>
                            <span className="incident-report-radio-title">Use Existing Customer</span>
                            <p className="incident-report-radio-description">Select from existing store owners</p>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {loadExisting && (
                    <div className="incident-report-group">
                      <label htmlFor="owner_select">Select Store Owner<span className="required">*</span></label>
                      <div className="incident-report-select-wrapper">
                        <select 
                          id="owner_select"
                          className={`incident-report-select ${formErrors.selectedOwnerId ? 'error' : ''}`}
                          value={selectedOwnerId || ''}
                          onChange={handleOwnerSelect}
                          disabled={isLoading}
                        >
                          <option value="">Select Owner</option>
                          {storeOwners.map(owner => (
                            <option key={owner.id} value={owner.id}>
                              {owner.user?.first_name} {owner.user?.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {formErrors.selectedOwnerId && <div className="incident-report-error">{formErrors.selectedOwnerId}</div>}
                    </div>
                  )}

                  {!loadExisting && (
                    <>
                      <div className="incident-report-group">
                        <label htmlFor="first_name">First Name<span className="required">*</span></label>
                        <input 
                          type="text"
                          id="first_name" 
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          className={`incident-report-input ${formErrors.first_name ? 'error' : ''}`}
                          placeholder="First Name" 
                          disabled={isLoading}
                        />
                        {formErrors.first_name && <div className="incident-report-error">{formErrors.first_name}</div>}
                      </div>
                      
                      <div className="incident-report-group">
                        <label htmlFor="last_name">Last Name<span className="required">*</span></label>
                        <input 
                          type="text"
                          id="last_name" 
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          className={`incident-report-input ${formErrors.last_name ? 'error' : ''}`}
                          placeholder="Last Name" 
                          disabled={isLoading}
                        />
                        {formErrors.last_name && <div className="incident-report-error">{formErrors.last_name}</div>}
                      </div>
                      
                      <div className="incident-report-group">
                        <label htmlFor="email">Email<span className="required">*</span></label>
                        <input 
                          type="email"
                          id="email" 
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`incident-report-input ${formErrors.email ? 'error' : ''}`}
                          placeholder="Email Address" 
                          disabled={isLoading}
                        />
                        {formErrors.email && <div className="incident-report-error">{formErrors.email}</div>}
                      </div>
                      
                      <div className="incident-report-group">
                        <label htmlFor="phone_number">Owner Phone Number</label>
                        <input 
                          type="text"
                          id="phone_number" 
                          name="phone_number"
                          value={formData.phone_number}
                          onChange={handleChange}
                          className="incident-report-input" 
                          placeholder="Owner Phone Number" 
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="incident-report-group">
                        <label>Owner Profile Image</label>
                        <div className="incident-report-file-container">
                          <label className="incident-report-file-upload">
                            <input 
                              type="file"
                              ref={ownerImageRef}
                              name="owner_image"
                              onChange={handleFileChange}
                              accept="image/*"
                              className="incident-report-file-input"
                              disabled={isLoading}
                            />
                            <div className="incident-report-upload-content">
                              <div className="incident-report-upload-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                              </div>
                              <div className="incident-report-upload-text">
                                <span>Upload owner image</span>
                                <p>Click to browse or drag files here</p>
                              </div>
                            </div>
                          </label>
                        </div>
                        {formData.owner_image_preview && (
                          <div className="incident-report-file-list">
                            <ul>
                              <li className="incident-report-file-item">
                                <img
                                  src={formData.owner_image_preview}
                                  alt="Owner preview"
                                  className="incident-report-file-preview"
                                />
                                <span className="incident-report-file-name">Owner Profile</span>
                                <button
                                  type="button"
                                  className="incident-report-delete-file"
                                  onClick={() => setFormData(prev => ({...prev, owner_image: null, owner_image_preview: null}))}
                                  aria-label="Delete file"
                                >
                                  &times;
                                </button>
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="incident-report-section">
                  <h2>Store Information</h2>

                  <div className="incident-report-group">
                    <label htmlFor="store_name">Store Name<span className="required">*</span></label>
                    <input 
                      type="text"
                      id="store_name" 
                      name="store_name"
                      value={formData.store_name}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.store_name ? 'error' : ''}`}
                      placeholder="Store Name" 
                      disabled={isLoading}
                    />
                    {formErrors.store_name && <div className="incident-report-error">{formErrors.store_name}</div>}
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="store_address">Store Address</label>
                    <textarea 
                      id="store_address"
                      name="store_address"
                      value={formData.store_address}
                      onChange={handleChange}
                      className="incident-report-textarea" 
                      placeholder="Street Address" 
                      disabled={isLoading}
                    ></textarea>
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="store_city">City<span className="required">*</span></label>
                    <input 
                      type="text"
                      id="store_city" 
                      name="store_city"
                      value={formData.store_city}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.store_city ? 'error' : ''}`}
                      placeholder="City" 
                      disabled={isLoading}
                    />
                    {formErrors.store_city && <div className="incident-report-error">{formErrors.store_city}</div>}
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="store_state">State<span className="required">*</span></label>
                    <input 
                      type="text"
                      id="store_state" 
                      name="store_state"
                      value={formData.store_state}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.store_state ? 'error' : ''}`}
                      placeholder="State" 
                      disabled={isLoading}
                    />
                    {formErrors.store_state && <div className="incident-report-error">{formErrors.store_state}</div>}
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="store_zipcode">Zipcode<span className="required">*</span></label>
                    <input 
                      type="text"
                      id="store_zipcode" 
                      name="store_zipcode"
                      value={formData.store_zipcode}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.store_zipcode ? 'error' : ''}`}
                      placeholder="Zipcode" 
                      disabled={isLoading}
                    />
                    {formErrors.store_zipcode && <div className="incident-report-error">{formErrors.store_zipcode}</div>}
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="store_phone_no">Store Phone No<span className="required">*</span></label>
                    <input 
                      type="text"
                      id="store_phone_no" 
                      name="store_phone_no"
                      value={formData.store_phone_no}
                      onChange={handleChange}
                      className={`incident-report-input ${formErrors.store_phone_no ? 'error' : ''}`}
                      placeholder="Store Phone Number" 
                      disabled={isLoading}
                    />
                    {formErrors.store_phone_no && <div className="incident-report-error">{formErrors.store_phone_no}</div>}
                  </div>

                  {/* NEW: Incentive Rates Section */}
                  <div className="incident-report-group" style={{ marginTop: '32px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '12px', border: '2px solid #e0e7ff' }}>
                    <label style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '12px', display: 'block' }}>
                      💰 Incentive Rates by Incident Type
                    </label>
                    <div className="incident-report-helper-text" style={{ marginBottom: '20px' }}>
                      Set the incentive amount paid to employees for each type of approved report. Different incident types can have different reward amounts.
                    </div>
                    
                    {INCIDENT_TYPES.map((type) => (
                      <div key={type.value} style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '20px', minWidth: '30px' }}>{type.icon}</span>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>
                            {type.label}
                          </label>
                        </div>
                        <div className="incident-report-input-wrapper" style={{ width: '150px' }}>
                          <span className="incident-report-currency-symbol">₹</span>
                          <input
                            type="text"
                            value={incentiveRates[type.value]}
                            onChange={(e) => handleIncentiveRateChange(type.value, e.target.value)}
                            className={`incident-report-input incident-report-currency-input ${formErrors[`incentive_${type.value}`] ? 'error' : ''}`}
                            placeholder="0.00"
                            disabled={isLoading}
                          />
                        </div>
                        {formErrors[`incentive_${type.value}`] && (
                          <div className="incident-report-error">{formErrors[`incentive_${type.value}`]}</div>
                        )}
                      </div>
                    ))}
                    
                    <div style={{ 
                      marginTop: '20px', 
                      padding: '16px', 
                      backgroundColor: '#e0f2fe', 
                      borderRadius: '8px',
                      border: '1px solid #0891b2'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e' }}>
                          💡 Total Potential Earnings (All Types):
                        </span>
                        <span style={{ fontSize: '20px', fontWeight: '700', color: '#0c4a6e' }}>
                          ₹{calculateTotalIncentive()}
                        </span>
                      </div>
                      <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#0c4a6e' }}>
                        If an employee submits one report of each type
                      </p>
                    </div>
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="pos_name">POS Name</label>
                    <input 
                      type="text"
                      id="pos_name" 
                      name="pos_name"
                      value={formData.pos_name}
                      onChange={handleChange}
                      className="incident-report-input" 
                      placeholder="POS Company Name" 
                      disabled={isLoading}
                    />
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="pos_qty">POS Quantity</label>
                    <input 
                      type="number"
                      id="pos_qty" 
                      name="pos_qty"
                      value={formData.pos_qty}
                      onChange={handleChange}
                      className="incident-report-input" 
                      placeholder="POS Quantity" 
                      min="1"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="incident-report-group">
                    <label>Store Category (Hours)</label>
                    <div className="incident-report-radio-options">
                      <label className="incident-report-radio-option small">
                        <input
                          type="radio"
                          name="store_category"
                          value="24x7"
                          checked={formData.store_category === "24x7"}
                          onChange={() => handleCategoryChange("24x7")}
                        />
                        <div className="incident-report-radio-content">
                          <div className="incident-report-radio-check"></div>
                          <span className="incident-report-radio-title">24x7</span>
                        </div>
                      </label>
                      
                      <label className="incident-report-radio-option small">
                        <input
                          type="radio"
                          name="store_category"
                          value="other"
                          checked={formData.store_category === "other"}
                          onChange={() => handleCategoryChange("other")}
                        />
                        <div className="incident-report-radio-content">
                          <div className="incident-report-radio-check"></div>
                          <span className="incident-report-radio-title">Other</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="nest_account">Nest Account</label>
                    <input 
                      type="text"
                      id="nest_account" 
                      name="nest_account"
                      value={formData.nest_account}
                      onChange={handleChange}
                      className="incident-report-input" 
                      placeholder="Email Address" 
                      disabled={isLoading}
                    />
                  </div>

                  <div className="incident-report-group">
                    <label>Store Image</label>
                    <div className="incident-report-file-container">
                      <label className="incident-report-file-upload">
                        <input 
                          type="file"
                          ref={storeImageRef}
                          name="image"
                          onChange={handleFileChange}
                          accept="image/*"
                          className="incident-report-file-input"
                          disabled={isLoading}
                        />
                        <div className="incident-report-upload-content">
                          <div className="incident-report-upload-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                          </div>
                          <div className="incident-report-upload-text">
                            <span>Upload store image</span>
                            <p>Click to browse or drag files here</p>
                          </div>
                        </div>
                      </label>
                    </div>
                    {formData.image_preview && (
                      <div className="incident-report-file-list">
                        <ul>
                          <li className="incident-report-file-item">
                            <img
                              src={formData.image_preview}
                              alt="Store preview"
                              className="incident-report-file-preview"
                            />
                            <span className="incident-report-file-name">Store Image</span>
                            <button
                              type="button"
                              className="incident-report-delete-file"
                              onClick={() => setFormData(prev => ({...prev, image: null, image_preview: null}))}
                              aria-label="Delete file"
                            >
                              &times;
                            </button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="specialComments">Special Comments</label>
                    <textarea 
                      id="specialComments"
                      name="specialComments"
                      value={formData.specialComments}
                      onChange={handleChange}
                      className="incident-report-textarea" 
                      placeholder="Type any special comments or notes here..."
                      disabled={isLoading}
                    ></textarea>
                  </div>
                </div>

                {/* Store Notes Section - Only show in edit mode */}
                {isEditing && (
                  <div className="incident-report-section">
                    <h2>Store Notes & Updates</h2>
                    <div className="incident-report-helper-text">
                      Add important notes and updates about this store. All employees can view these notes.
                    </div>
                    
                    <StoreNotesManager storeId={storeId} />
                  </div>
                )}

                {/* Email Configuration Section */}
                <div className="incident-report-section">
                  <h2>Notification Email Configuration</h2>
                  <div className="incident-report-helper-text">
                    Configure email addresses that will receive notifications when reports are approved for this store.
                  </div>

                  <div className="incident-report-group">
                    <label className="incident-report-checkbox-label">
                      <input
                        type="checkbox"
                        name="use_owner_email_fallback"
                        checked={formData.use_owner_email_fallback}
                        onChange={handleChange}
                      />
                      <span className="incident-report-checkbox"></span>
                      Use store owner email as fallback if no recipients configured
                    </label>
                  </div>

                  <div className="incident-report-group">
                    <label>Primary Recipients</label>
                    <div className="incident-report-helper-text">
                      Primary recipients who will receive report approval notifications.
                    </div>
                    
                    <div className="incident-report-email-input-group">
                      <div className="incident-report-email-input-wrapper">
                        <input
                          type="email"
                          value={emailInputs.newRecipient}
                          onChange={(e) => handleEmailInputChange('newRecipient', e.target.value)}
                          onKeyPress={(e) => handleEmailKeyPress(e, 'recipient')}
                          className={`incident-report-input ${emailErrors.newRecipient ? 'error' : ''}`}
                          placeholder="Enter email address"
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="incident-report-btn-add-email"
                          onClick={addEmailRecipient}
                          disabled={isLoading || !emailInputs.newRecipient.trim()}
                        >
                          Add Recipient
                        </button>
                      </div>
                      {emailErrors.newRecipient && (
                        <div className="incident-report-error">{emailErrors.newRecipient}</div>
                      )}
                    </div>

                    {formData.approval_email_recipients.length > 0 && (
                      <div className="incident-report-email-list">
                        <h4>Primary Recipients ({formData.approval_email_recipients.length})</h4>
                        <div className="incident-report-email-tags">
                          {formData.approval_email_recipients.map((email, index) => (
                            <div key={index} className="incident-report-email-tag">
                              <span className="incident-report-email-text">{email}</span>
                              <button
                                type="button"
                                className="incident-report-email-remove"
                                onClick={() => removeEmailRecipient(email)}
                                disabled={isLoading}
                                aria-label={`Remove ${email}`}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="incident-report-group">
                    <label>CC Recipients</label>
                    <div className="incident-report-helper-text">
                      Additional recipients who will be CC'd on report approval notifications.
                    </div>
                    
                    <div className="incident-report-email-input-group">
                      <div className="incident-report-email-input-wrapper">
                        <input
                          type="email"
                          value={emailInputs.newCC}
                          onChange={(e) => handleEmailInputChange('newCC', e.target.value)}
                          onKeyPress={(e) => handleEmailKeyPress(e, 'cc')}
                          className={`incident-report-input ${emailErrors.newCC ? 'error' : ''}`}
                          placeholder="Enter CC email address"
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="incident-report-btn-add-email"
                          onClick={addCCRecipient}
                          disabled={isLoading || !emailInputs.newCC.trim()}
                        >
                          Add CC
                        </button>
                      </div>
                      {emailErrors.newCC && (
                        <div className="incident-report-error">{emailErrors.newCC}</div>
                      )}
                    </div>

                    {formData.approval_email_cc.length > 0 && (
                      <div className="incident-report-email-list">
                        <h4>CC Recipients ({formData.approval_email_cc.length})</h4>
                        <div className="incident-report-email-tags">
                          {formData.approval_email_cc.map((email, index) => (
                            <div key={index} className="incident-report-email-tag incident-report-email-tag-cc">
                              <span className="incident-report-email-text">{email}</span>
                              <button
                                type="button"
                                className="incident-report-email-remove"
                                onClick={() => removeCCRecipient(email)}
                                disabled={isLoading}
                                aria-label={`Remove CC ${email}`}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {formErrors.email_config && (
                    <div className="incident-report-error">{formErrors.email_config}</div>
                  )}

                  <div className="incident-report-email-summary">
                    <div className="incident-report-helper-text">
                      <strong>Summary:</strong> {formData.approval_email_recipients.length} primary recipients, {formData.approval_email_cc.length} CC recipients
                      {formData.use_owner_email_fallback && formData.approval_email_recipients.length === 0 && (
                        <span>, store owner email as fallback</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="incident-report-actions">
                  <button 
                    type="button" 
                    className="incident-report-btn-secondary"
                    onClick={() => navigate('/stores')}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <div className="incident-report-right-actions">
                    <button 
                      type="submit" 
                      className="incident-report-btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Processing...' : isEditing ? 'Update Store' : 'Create Store Account'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <StoreCreationModal
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        storeData={creationResponse}
        storeOwnerCredentials={creationResponse?.store_owner_credentials}
        isNewOwnerCreated={creationResponse?.new_store_owner_created || false}
      />

      <style jsx>{`
        .incident-report-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .incident-report-currency-symbol {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #666;
          font-weight: 500;
          z-index: 1;
          pointer-events: none;
        }

        .incident-report-currency-input {
          padding-left: 30px !important;
        }

        .incident-report-helper-text {
          font-size: 0.875rem;
          color: #6b7280;
          margin-top: 4px;
          line-height: 1.4;
        }

        .incident-report-group .incident-report-helper-text:first-of-type {
          margin-top: 0;
          margin-bottom: 8px;
        }

        .incident-report-email-input-group {
          margin-bottom: 16px;
        }

        .incident-report-email-input-wrapper {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }

        .incident-report-email-input-wrapper .incident-report-input {
          flex: 1;
        }

        .incident-report-btn-add-email {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
          white-space: nowrap;
        }

        .incident-report-btn-add-email:hover:not(:disabled) {
          background: #2563eb;
        }

        .incident-report-btn-add-email:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .incident-report-email-list {
          margin-top: 12px;
          padding: 16px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .incident-report-email-list h4 {
          margin: 0 0 12px 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .incident-report-email-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .incident-report-email-tag {
          display: flex;
          align-items: center;
          background: #e0f2fe;
          border: 1px solid #0891b2;
          color: #0c4a6e;
          padding: 6px 8px;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .incident-report-email-tag-cc {
          background: #fef3c7;
          border-color: #f59e0b;
          color: #92400e;
        }

        .incident-report-email-text {
          margin-right: 8px;
        }

        .incident-report-email-remove {
          background: none;
          border: none;
          color: inherit;
          font-size: 16px;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s;
        }

        .incident-report-email-remove:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .incident-report-email-summary {
          margin-top: 16px;
          padding: 12px;
          background: #f1f5f9;
          border-radius: 6px;
          border-left: 4px solid #3b82f6;
        }
      `}</style>
    </div>
  );
};

export default StoreManagementForm;